/*============================================================================
  COMPONENT: related-products-renderer
  Owner: snippets/related-products.liquid
  Contract: docs/DOM_HOOK_CONTRACTS.md (Related Products section)

  Layers:
    - CardBuilder: DOM construction only (no fetch)
    - FetchLayer: Shopify recommendations API
    - init(): orchestration entry point
==============================================================================*/
window.AntistateRelatedProducts = (function() {
  'use strict';

  /* ---------- CARD BUILDER LAYER (DOM construction only) ---------- */

  function formatMoney(cents, moneyFormat) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    var dollars = Number(cents || 0) / 100;
    return '$' + dollars.toFixed(2);
  }

  function productImage(product) {
    if (typeof product.featured_image === 'string' && product.featured_image.length) {
      return product.featured_image;
    }
    if (product.featured_image && product.featured_image.url) {
      return product.featured_image.url;
    }
    if (Array.isArray(product.images) && product.images.length) {
      return product.images[0];
    }
    return '';
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url;
    return url;
  }

  function getProductUrl(product) {
    if (!product) return '';
    if (typeof product.url === 'string' && product.url.length) {
      return product.url;
    }
    if (typeof product.handle === 'string' && product.handle.length) {
      return '/products/' + encodeURIComponent(product.handle);
    }
    return '';
  }

  function getVariantId(product) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) {
      return null;
    }
    var variant = product.variants[0];
    var variantId = variant && variant.id ? Number(variant.id) : null;
    return variantId || null;
  }

  function isSingleVariant(product) {
    return !!(product && Array.isArray(product.variants) && product.variants.length === 1);
  }

  function isAvailable(product) {
    return product && product.available !== false;
  }

  function createOverlayAction(tagName, className, label, href) {
    var node = document.createElement(tagName);
    node.className = className;
    node.setAttribute('aria-label', label);
    if (tagName === 'a' && href) {
      node.href = href;
      node.setAttribute('data-product-nav', '');
    }
    return node;
  }

  function createOverlayActions(product, productUrl, isSoldOut, icons) {
    var actionWrap = document.createElement('div');
    actionWrap.className = 'product__image-actions';
    actionWrap.setAttribute('aria-label', 'Quick product actions');

    var viewAction = createOverlayAction(
      'a',
      'product__overlay-action product__overlay-action--view',
      'See options for ' + (product.title || 'product'),
      productUrl
    );
    viewAction.innerHTML = icons.scan;
    actionWrap.appendChild(viewAction);

    if (isSoldOut) {
      var disabledAdd = createOverlayAction(
        'button',
        'product__overlay-action product__overlay-action--add',
        (product.title || 'Product') + ' sold out'
      );
      disabledAdd.type = 'button';
      disabledAdd.disabled = true;
      disabledAdd.setAttribute('aria-disabled', 'true');
      disabledAdd.innerHTML = icons.plus;
      actionWrap.appendChild(disabledAdd);
      return actionWrap;
    }

    if (isSingleVariant(product)) {
      var quickAdd = createOverlayAction(
        'button',
        'product__overlay-action product__overlay-action--add product__quick-add-btn',
        'Add ' + (product.title || 'product') + ' to cart'
      );
      var variantId = getVariantId(product);
      quickAdd.type = 'button';
      quickAdd.setAttribute('data-quick-add', 'true');
      if (variantId) {
        quickAdd.setAttribute('data-variant-id', String(variantId));
      }
      quickAdd.setAttribute('data-product-url', productUrl);
      quickAdd.innerHTML = icons.plus;
      actionWrap.appendChild(quickAdd);
      return actionWrap;
    }

    var chooseAction = createOverlayAction(
      'button',
      'product__overlay-action product__overlay-action--options product__quick-options-btn',
      'Choose options for ' + (product.title || 'product'),
      ''
    );
    chooseAction.type = 'button';
    chooseAction.setAttribute('data-quick-options', 'true');
    chooseAction.setAttribute('data-product-handle', product.handle || '');
    chooseAction.setAttribute('data-product-url', productUrl);
    chooseAction.setAttribute('data-product-title', product.title || '');
    chooseAction.innerHTML = icons.eye;
    actionWrap.appendChild(chooseAction);
    return actionWrap;
  }

  function createLegacyQuickAdd(product, productUrl, isSoldOut, strings) {
    var wrap = document.createElement('div');
    wrap.className = 'product__quick-add text-center';

    if (isSoldOut) {
      var soldOutButton = document.createElement('button');
      soldOutButton.type = 'button';
      soldOutButton.className = 'btn btn--secondary product__quick-add-btn';
      soldOutButton.disabled = true;
      soldOutButton.setAttribute('aria-disabled', 'true');
      soldOutButton.textContent = strings.soldOut;
      wrap.appendChild(soldOutButton);
      return wrap;
    }

    if (isSingleVariant(product)) {
      var quickAdd = document.createElement('button');
      var variantId = getVariantId(product);
      quickAdd.type = 'button';
      quickAdd.className = 'btn product__quick-add-btn';
      quickAdd.setAttribute('data-quick-add', 'true');
      if (variantId) {
        quickAdd.setAttribute('data-variant-id', String(variantId));
      }
      quickAdd.setAttribute('data-product-url', productUrl);
      quickAdd.setAttribute('aria-label', 'Add ' + (product.title || 'product') + ' to cart');
      quickAdd.textContent = strings.addToCart;
      wrap.appendChild(quickAdd);
      return wrap;
    }

    var chooseLink = document.createElement('a');
    chooseLink.className = 'btn btn--secondary product__quick-add-btn';
    chooseLink.href = productUrl;
    chooseLink.setAttribute('data-product-nav', '');
    chooseLink.setAttribute('aria-label', 'Choose options for ' + (product.title || 'product'));
    chooseLink.textContent = strings.chooseOptions;
    wrap.appendChild(chooseLink);
    return wrap;
  }

  function buildCard(product, config) {
    var productUrl = getProductUrl(product);
    if (!productUrl) return null;

    var isSoldOut = !isAvailable(product);
    var image = normalizeImageUrl(productImage(product));
    var aspectRatio = (product && product.featured_image && Number(product.featured_image.aspect_ratio)) || 1;
    if (!aspectRatio || aspectRatio <= 0) {
      aspectRatio = 1;
    }
    var priceValue = Number(product.price_min || product.price || 0);

    var card = document.createElement('div');
    card.className = 'product grid__item medium-up--one-third small--one-half slide-up-animation animated';
    card.setAttribute('role', 'listitem');
    if (config.iconOverlayEnabled) {
      card.className += ' product--icon-actions';
    }

    var mediaWrap = document.createElement('div');
    mediaWrap.className = 'supports-js product__media-wrap';
    mediaWrap.style.maxWidth = Math.round(600 * aspectRatio) + 'px';
    mediaWrap.style.margin = '0 auto';

    var imageLink = document.createElement('a');
    imageLink.href = productUrl;
    imageLink.className = 'product__image-wrapper';
    imageLink.setAttribute('title', product.title || '');
    imageLink.setAttribute('aria-label', product.title || 'Product');
    imageLink.setAttribute('data-product-nav', '');
    imageLink.style.paddingTop = String((1 / aspectRatio) * 100) + '%';

    if (image) {
      var img = document.createElement('img');
      img.className = 'product__image';
      img.src = image;
      img.alt = product.title || 'Product image';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.draggable = false;
      img.style.maxWidth = Math.round(600 * aspectRatio) + 'px';
      img.style.maxHeight = '600px';
      imageLink.appendChild(img);
    }

    mediaWrap.appendChild(imageLink);
    if (config.iconOverlayEnabled) {
      mediaWrap.appendChild(createOverlayActions(product, productUrl, isSoldOut, config.icons));
    }
    card.appendChild(mediaWrap);

    var titleWrap = document.createElement('div');
    titleWrap.className = 'product__title text-center';
    var titleLink = document.createElement('a');
    titleLink.href = productUrl;
    titleLink.setAttribute('aria-label', product.title || 'Product');
    titleLink.setAttribute('data-product-nav', '');
    titleLink.textContent = product.title || '';
    titleWrap.appendChild(titleLink);
    card.appendChild(titleWrap);

    var priceWrap = document.createElement('div');
    priceWrap.className = 'product__prices text-center';
    var price = document.createElement('span');
    price.className = 'product__price';
    price.textContent = formatMoney(priceValue, config.moneyFormat);
    priceWrap.appendChild(price);
    if (isSoldOut) {
      priceWrap.appendChild(document.createTextNode(' — '));
      var soldOut = document.createElement('strong');
      soldOut.className = 'sold-out-text';
      soldOut.textContent = config.strings.soldOut;
      priceWrap.appendChild(soldOut);
    }
    card.appendChild(priceWrap);

    if (!config.iconOverlayEnabled) {
      card.appendChild(createLegacyQuickAdd(product, productUrl, isSoldOut, config.strings));
    }

    return card;
  }

  /* ---------- FETCH LAYER (API only, no DOM construction) ---------- */

  function fetchRecommendations(productId, limit, intent, currentHandle) {
    var endpoint = '/recommendations/products.json?product_id='
      + encodeURIComponent(productId)
      + '&limit=' + encodeURIComponent(limit)
      + '&intent=' + encodeURIComponent(intent);
    return fetch(endpoint, { credentials: 'same-origin' })
      .then(function(response) {
        if (!response.ok) throw new Error('Recommendations request failed');
        return response.json();
      })
      .then(function(payload) {
        var products = (payload && payload.products) ? payload.products : [];
        return products.filter(function(p) {
          return p && p.handle && p.handle !== currentHandle;
        });
      });
  }

  function resolveRecommendations(productId, limit, currentHandle) {
    return fetchRecommendations(productId, limit, 'complementary', currentHandle)
      .then(function(products) {
        if (!products.length) {
          return fetchRecommendations(productId, limit, 'related', currentHandle)
            .then(function(relatedProducts) {
              return { intent: 'related', products: relatedProducts };
            });
        }
        return { intent: 'complementary', products: products };
      });
  }

  function setRecommendationTitle(titleNode, intent, defaultTitle) {
    if (!titleNode) return;
    if (intent === 'complementary') {
      titleNode.textContent = 'Complete the kit';
    } else if (intent === 'related') {
      titleNode.textContent = 'You might also like';
    } else {
      titleNode.textContent = defaultTitle;
    }
  }

  /* ---------- ORCHESTRATION ---------- */

  function init(root, config) {
    if (!root || root.getAttribute('data-related-init') === '1') return;
    root.setAttribute('data-related-init', '1');

    var grid = root.querySelector('[data-related-grid]');
    if (!grid) return;

    var hasFallbackItems = grid.children.length > 0;
    var titleNode = root.querySelector('[data-rec-title]');

    resolveRecommendations(config.productId, config.limit, config.currentHandle)
      .then(function(result) {
        var products = (result && result.products) ? result.products : [];
        if (!products.length) {
          if (!hasFallbackItems) {
            root.style.display = 'none';
          }
          return;
        }
        setRecommendationTitle(titleNode, result.intent, config.defaultTitle);
        while (grid.firstChild) {
          grid.removeChild(grid.firstChild);
        }

        products.forEach(function(p) {
          var card = buildCard(p, config);
          if (card) {
            grid.appendChild(card);
          }
        });

        if (!grid.children.length && !hasFallbackItems) {
          root.style.display = 'none';
        }
      })
      .catch(function() {
        if (!hasFallbackItems) {
          root.style.display = 'none';
        }
      });
  }

  return {
    init: init,
    buildCard: buildCard,
    fetchRecommendations: fetchRecommendations
  };
})();